import React, { useState } from 'react';
import { CustomerModel } from '@app/interfaces/customer';
import { Button, Descriptions, Image, Tag, Tooltip } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { usePermissions } from '@app/hooks/usePermissions';
import { BsFillBuildingsFill } from 'react-icons/bs';
import { FaMapMarker } from 'react-icons/fa';
import GoogleMapView from '@app/components/common/GoogleMapView';
import { formatBirthday } from '@app/utils/utils';
import { Link } from 'react-router-dom';
import { getFullBlobUrl } from '@app/utils/blobUrl';
import { noAvatar } from 'constants/noAvatar';
import UserAvatar from '@app/components/common/UserAvatar/UserAvatar';

interface CustomerInfoProps {
  record: CustomerModel;
  source?: string;
}

const LABEL_STYLE = { fontWeight: 500, width: 180, background: '#fafafa', color: '#595959' };
const COLS = { xs: 1, sm: 2, md: 2 };

const Overview: React.FC<CustomerInfoProps> = ({ record }) => {
  const { can } = usePermissions();
  const canViewPhone = can('customers', 'show');
  const [phoneVisible, setPhoneVisible] = useState(false);

  return (
    <div className="tw-p-4">
      <div className="tw-mb-4 tw-flex tw-justify-center">
        <Image
          width={120}
          height={120}
          src={getFullBlobUrl(record.avatar_url) || noAvatar}
          fallback={noAvatar}
          className="tw-rounded-xl tw-border tw-border-solid tw-border-[#f0f0f0] tw-object-cover"
        />
      </div>

      <Descriptions bordered size="small" column={COLS} labelStyle={LABEL_STYLE} className="tw-mb-3">
        <Descriptions.Item label="Mã khách hàng">
          <Link to={`/partners/customers/${record.id}`} target="_blank" className="text-primary font-bold-weight">
            {record.customer_code}
          </Link>
        </Descriptions.Item>
        <Descriptions.Item label="Nhóm khách hàng">
          <Tag color="blue">{record.customer_group?.name}</Tag>
        </Descriptions.Item>

        <Descriptions.Item label="Tên khách hàng">
          <span className="text-primary font-bold-weight">{record.name}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Loại khách hàng">
          <span className="text-primary">{record.customer_type_name}</span>
        </Descriptions.Item>

        <Descriptions.Item label="Ngày sinh (DD-MM)">
          <span className="text-primary">{formatBirthday(record.date_of_birth)}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Phái">
          <span className="text-primary">{record.gender_name}</span>
        </Descriptions.Item>

        <Descriptions.Item label="Điện thoại">
          {canViewPhone && record.phone ? (
            <span>
              {phoneVisible ? record.phone : '••••••••••'}
              <Tooltip title={phoneVisible ? 'Ẩn số điện thoại' : 'Hiện số điện thoại'}>
                <Button
                  type="link"
                  size="small"
                  icon={phoneVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={() => setPhoneVisible((v) => !v)}
                />
              </Tooltip>
            </span>
          ) : (
            <span className="text-secondary">{record.phone ? '••••••••••' : '—'}</span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Email">
          <span className="text-primary">{record.email || '—'}</span>
        </Descriptions.Item>

        <Descriptions.Item label="Facebook">
          <span className="text-primary">{record.facebook || '—'}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Ghi chú">
          <span className="text-primary">{record.remark || '—'}</span>
        </Descriptions.Item>

        <Descriptions.Item label="Địa chỉ" span={2}>
          <GoogleMapView address={record.full_address} />
        </Descriptions.Item>
      </Descriptions>

      <Descriptions bordered size="small" column={COLS} labelStyle={LABEL_STYLE} className="tw-mb-3">
        <Descriptions.Item label={<><BsFillBuildingsFill /> Tên công ty/Trường học</>}>
          <span className="text-primary">{record.company_name || '—'}</span>
        </Descriptions.Item>
        <Descriptions.Item label={<><FaMapMarker /> Chức vụ</>}>
          <span className="text-primary">{record.title || '—'}</span>
        </Descriptions.Item>

        <Descriptions.Item label={<><FaMapMarker /> Địa chỉ công ty/Trường học</>} span={2}>
          <GoogleMapView address={record.company_address} />
        </Descriptions.Item>
      </Descriptions>

      <Descriptions bordered size="small" column={COLS} labelStyle={LABEL_STYLE} className="tw-mb-3">
        <Descriptions.Item label="Người phụ trách bổ sung" span={2}>
          {record.assigned_to?.length
            ? record.assigned_to.map((item: string) => (
                <Tag color="blue" key={item}>{item}</Tag>
              ))
            : <span className="tw-text-[#bfbfbf]">—</span>}
        </Descriptions.Item>
        <Descriptions.Item label="Người tạo">
          <UserAvatar name={record.created_by} avatarUrl={record.created_by_avatar_url} />
        </Descriptions.Item>
        <Descriptions.Item label="Ngày tạo">
          <span className="text-primary">{record.created_date}</span>
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
};

export default Overview;
